import { Suspense, useEffect, useRef, useState } from "react"
import { FormikProps } from "formik"
import { Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useNavigate, useParams } from "react-router-dom"

import { MERCHANT_ADMIN_PRICE_LISTS_ROUTE, MERCHANT_ADMIN_ROUTE } from "../../../../../constants/routes"
import { QueryErrorBoundary, QueryFallbackMessage } from "../../../../../core/api/components/QueryErrorBoundary"
import { ErrorDisplay } from "../../../../../core/api/errorTypes"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { StandardLoadingButton } from "../../../../../shared/components/buttons/LoadingButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { LoadingSpinner } from "../../../../../shared/components/loading/LoadingSpinner"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"

import { AdjustPricesFormController } from "../components/adjustPrices/AdjustPricesFormController"
import {
    AdjustPricesFormValues,
    countPricedProductsInList,
    getPriceListCurrency,
} from "../components/adjustPrices/adjustPricesLib"
import { fetchDetailedPriceLists, FetchDetailedPriceListsResponse, PriceListQuery } from "../priceListApi"

const useStyles = makeStyles()((theme) => ({
    subtitle: {
        color: theme.palette.tailwind.slate[500],
    },
    headerButton: {
        whiteSpace: "nowrap",
    },
}))

const selectPriceListHeading = ({ priceList }: FetchDetailedPriceListsResponse) => ({
    name: priceList.name,
    itemCount: countPricedProductsInList(priceList.productGroups),
    isAdjustable: getPriceListCurrency(priceList) !== undefined,
})

export function AdjustPriceListPrices() {
    const { classes } = useStyles()
    const navigate = useNavigate()
    const { priceListId } = useParams()
    const formRef = useRef<FormikProps<AdjustPricesFormValues>>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isSubmitBlocked, setIsSubmitBlocked] = useState(false)

    const id = Number(priceListId)
    const isValidId = Number.isFinite(id)

    useEffect(() => {
        if (!isValidId) navigate(MERCHANT_ADMIN_PRICE_LISTS_ROUTE, { replace: true })
    }, [isValidId, navigate])

    const { data } = useApiQuery(fetchDetailedPriceLists, {
        queryName: PriceListQuery.DETAILS,
        dependencies: { id },
        select: selectPriceListHeading,
        enabled: isValidId,
        errorDisplay: ErrorDisplay.NONE,
    })

    const itemCount = data?.itemCount ?? 0

    const handleAdjustClick = async () => {
        setIsSubmitting(true)
        try {
            await formRef.current?.submitForm()
        } catch {
            // useApiMutation has already shown the error snackbar
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <>
            <LocalizedStrict id="adjust-price-list-page-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.MERCHANT_PRICE_LIST}
                    title="Adjust prices"
                    subtitleElement={
                        <LocalizedStrict
                            id="adjust-price-list-page-subtitle"
                            vars={{ priceList: data?.name ?? "", count: itemCount }}
                        >
                            <Typography variant="body2" className={classes.subtitle}>
                                {data?.name} · {itemCount} items
                            </Typography>
                        </LocalizedStrict>
                    }
                    rightElement={
                        <Stack direction="row" spacing="1rem">
                            <LocalizedStrict id="adjust-price-list-cancel-button">
                                <StandardButton
                                    variant="outlined"
                                    className={classes.headerButton}
                                    onClick={() => navigate(MERCHANT_ADMIN_PRICE_LISTS_ROUTE)}
                                >
                                    Cancel
                                </StandardButton>
                            </LocalizedStrict>
                            <LocalizedStrict id="adjust-price-list-submit-button">
                                <StandardLoadingButton
                                    loading={isSubmitting}
                                    disabled={isSubmitBlocked || !data?.isAdjustable}
                                    className={classes.headerButton}
                                    onClick={handleAdjustClick}
                                >
                                    Adjust prices
                                </StandardLoadingButton>
                            </LocalizedStrict>
                        </Stack>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>

                        <LocalizedStrict id="merchant-nav-item-products-header">
                            <Breadcrumb>Products</Breadcrumb>
                        </LocalizedStrict>

                        <LocalizedStrict id="merchant-nav-item-price-lists">
                            <BreadcrumbLink to={MERCHANT_ADMIN_PRICE_LISTS_ROUTE}>Price Lists</BreadcrumbLink>
                        </LocalizedStrict>

                        <LocalizedStrict id="adjust-price-list-breadcrumb">
                            <Breadcrumb>Adjust prices</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <QueryErrorBoundary
                dependencies={{ id }}
                noDataFallback={
                    <LocalizedStrict id="adjust-price-list-no-prices">
                        <QueryFallbackMessage>This price list has no prices to adjust.</QueryFallbackMessage>
                    </LocalizedStrict>
                }
            >
                <Suspense fallback={<LoadingSpinner />}>
                    {isValidId && (
                        <AdjustPricesFormController
                            ref={formRef}
                            priceListId={id}
                            onSubmitBlockedChange={setIsSubmitBlocked}
                        />
                    )}
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
